import { memo } from "react";
import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
  /** Width of the skeleton (e.g., "100%", "200px") */
  width?: string;
  /** Height of the skeleton (e.g., "20px") */
  height?: string;
  /**
   * Shape variant.
   * - `rectangular` — no radius, full-block placeholder
   * - `circular` — rounded-full (avatar placeholder)
   * - `rounded` — radius-lg, default card/section placeholder
   * - `line` — 12px height + radius-md, text-line placeholder (DS `.skel-line`)
   */
  variant?: "rectangular" | "circular" | "rounded" | "line";
}

/**
 * Skeleton — shimmer loading placeholder.
 * DS spec: muted bg base + translating highlight overlay (shimmer keyframe in
 * utilities.css: translateX -100% → 100%, 1.6s linear infinite).
 * `line` variant = 12px height, radius-md, matches DS `.skel-line` spec.
 * Reduced-motion: globals.css collapses all animations to 0.01ms automatically.
 */
export const Skeleton = memo(function Skeleton({
  className,
  width,
  height,
  variant = "rounded",
}: SkeletonProps) {
  const variantClasses: Record<NonNullable<SkeletonProps["variant"]>, string> = {
    rectangular: "rounded-none",
    circular: "rounded-full",
    rounded: "rounded-[var(--radius-lg)]",
    line: "rounded-[var(--radius-md)] h-3",
  };

  return (
    <div
      role="presentation"
      aria-hidden="true"
      className={cn(
        "relative overflow-hidden bg-[var(--muted)]",
        variantClasses[variant],
        className
      )}
      style={{
        width: width?.startsWith("w-") ? undefined : width,
        height: variant === "line" ? undefined : (height?.startsWith("h-") ? undefined : height),
      }}
    >
      {/* Shimmer highlight sweep — translateX(-100%→100%) via utilities.css `shimmer` keyframe */}
      <span
        className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-[oklch(0.93_0.01_240/0.6)] to-transparent"
        aria-hidden="true"
      />
    </div>
  );
});
